import { type, type Type } from 'arktype'
import { AsYouType, type CountryCode, parsePhoneNumberFromString, type PhoneNumber } from 'libphonenumber-js/max'

import { failure, isSuccess, type Result, success } from '@orus.eu/result'

// Re-exported from `/max`: each libphonenumber-js entry point bundles its own copy of the metadata
export { type CountryCode, getCountries, getCountryCallingCode, type PhoneNumber } from 'libphonenumber-js/max'

/**
 * Dialled like French numbers, but separate countries for libphonenumber (`0692…` is Réunion). Overseas first, since
 * libphonenumber also accepts some of their landlines (`0596…`) as metropolitan.
 */
const FRENCH_NUMBERING_PLAN: readonly CountryCode[] = ['GP', 'MQ', 'GF', 'RE', 'YT', 'BL', 'MF', 'FR']

function getNumberingPlan(country: CountryCode): readonly CountryCode[] {
  return FRENCH_NUMBERING_PLAN.includes(country) ? FRENCH_NUMBERING_PLAN : [country]
}

/** France's plan includes its overseas regions: a Guadeloupe number is in it. */
export function isInNumberingPlanOf(phone: PhoneNumber, country: CountryCode): boolean {
  return phone.country !== undefined && getNumberingPlan(country).includes(phone.country)
}

function getCountriesToReadIn(nationalNumber: string, country: CountryCode): readonly CountryCode[] {
  // `08…` numbers are metropolitan, though libphonenumber accepts them in every overseas region too
  if (FRENCH_NUMBERING_PLAN.includes(country) && /^0?8/.test(nationalNumber.replace(/\D/g, ''))) return ['FR']
  return getNumberingPlan(country)
}

/** A national number (`06…`) is read in `defaultCountry`'s plan; without it, only `+…` and `00…` numbers parse. */
export function parsePhoneNumber(
  text: string,
  defaultCountry: CountryCode | undefined,
): Result<PhoneNumber, 'invalid-phone-number'> {
  const input = text.trim().replace(/^00/, '+')
  const countries = input.startsWith('+') || !defaultCountry ? [undefined] : getCountriesToReadIn(input, defaultCountry)
  for (const country of countries) {
    const phone = parsePhoneNumberFromString(
      input,
      country ? { defaultCountry: country, extract: false } : { extract: false },
    )
    if (phone?.isValid() && !phone.ext) return success(phone)
  }
  return failure('invalid-phone-number')
}

export function normalizePhoneNumber(text: string, defaultCountry: CountryCode | undefined): string {
  const parsed = parsePhoneNumber(text, defaultCountry)
  return isSuccess(parsed) ? parsed.output.number : text.trim()
}

/** Every form the number may be stored in, to find it by equality. */
export function getStoredPhoneNumberForms(text: string, defaultCountry: CountryCode | undefined): string[] {
  const parsed = parsePhoneNumber(text, defaultCountry)
  if (!isSuccess(parsed)) return [text]
  const national = parsed.output.formatNational()
  return [
    ...new Set([
      text,
      parsed.output.number,
      parsed.output.number.slice(1),
      national,
      national.replaceAll(' ', ''),
      national.replaceAll(' ', '.'),
      national.replaceAll(' ', '-'),
      parsed.output.formatInternational(),
    ]),
  ]
}

/** A request body phone with its country code, kept as written: the write path stores it in E.164. */
export const phoneNumberSchema: Type<string> = type('string').narrow((value, ctx) =>
  isSuccess(parsePhoneNumber(value, undefined))
    ? true
    : ctx.mustBe('a phone number with its country code, such as +33612345678'),
)

/** National format in the viewer's country, international elsewhere, unchanged when invalid (saved before E.164). */
export function formatPhoneNumber(text: string, viewerCountry: CountryCode): string {
  const parsed = parsePhoneNumber(text, viewerCountry)
  if (!isSuccess(parsed)) return text
  return parsed.output.country === viewerCountry ? parsed.output.formatNational() : parsed.output.formatInternational()
}

export function formatPhoneNumberAsYouType(text: string, country: CountryCode): string {
  return /^\+?[\d ]*$/.test(text) ? new AsYouType(country).input(text) : text
}
